// Design A ("Corporate Band") Quotation -- used ONLY for Print Preview (via
// PrintQuotationVoucher.jsx) when this company's institution_profile.
// print_template is 'design_a'. Mirrors
// backend/src/pdfTemplates/designA/quotationAdapter.js's config mapping
// exactly. QuotationVoucherBody.jsx (legacy, still used for the live
// entry-screen preview and for the 'legacy' print_template) is untouched.
//
// Carried over from the legacy template (see quotationAdapter.js for the
// full rationale): tax/discount rows only show when is_cal_type ===
// "on_total"; no Batch/HSN columns; no Paid/Due rows or bank details; a
// "Quantity Total" row; a separate Terms & Conditions page appended after
// the quotation when show_terms_conditions === "yes".
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { numberFormat, numberFormatTax, formatToCompanyTimezone } from "../../../lib/functions";
import { convertAmountToWordsDesignA } from "../../../lib/designAWords";
import { fitItemName } from "../../../lib/designAItemFit";
import { formatPercent } from "../../../lib/designANumberFormat";
import { salesQuotationWithRecordApi } from "../../../apiservice/sales";
import { getInstitutionApi } from "../../../apiservice/setting";
import VoucherBodyDesignA from "../../../components/common/VoucherBodyDesignA";

// Row budgets recalibrated for the taller "rich item" row (name line + an
// optional description sub-line, see da-table--rich-item in
// VoucherBodyDesignA.css) -- ~42px/row vs. the default 28px, so ~2/3 as
// many rows fit per page. Mirrors quotationAdapter.js exactly.
const QUOTATION_ROW_BUDGETS = { single: 10, first: 16, middle: 20, last: 9 };

// Same fixed-layout column widths as quotationAdapter.js -- see that file's
// comment for the reasoning. Keep both in sync.
const PAGE_CONTENT_WIDTH = 698;
// quantity shrunk from 72 to 44 now that its header is "Qty" not
// "Quantity" -- "Quantity" (8 chars) needed more room than 72px gave it
// and clipped to "QUANTI…" on a real print; "Qty" needs far less, freeing
// real width for the item name instead.
const COL_WIDTH = { hash: 36, rate: 100, per: 50, quantity: 44, amount: 104 };
const CELL_PADDING = 16;
const CHAR_WIDTH_PX = 5.3;

function ItemCell({ name, maxChars, batchName, description }) {
  const fit = fitItemName(name, { maxChars });
  const subParts = [];
  if (batchName) subParts.push(`Batch: ${batchName}`);
  if (description) subParts.push(description);
  return (
    <div className="da-item-cell">
      <span className="da-item-name" style={{ fontSize: `${fit.fontSizePercent}%` }}>{fit.text}</span>
      {subParts.length > 0 && <div className="da-item-sub">{subParts.join(" · ")}</div>}
    </div>
  );
}

function termsListItems(text) {
  return (text || "")
    .split("\n")
    .map((line) => line.replace(/^•\s*/, "").trim())
    .filter(Boolean);
}

function TermsPage({ record }) {
  if (record.show_terms_conditions !== "yes") return null;
  const terms = termsListItems(record.terms_conditions);
  const payment = termsListItems(record.payment_terms);
  if (!terms.length && !payment.length) return null;

  return (
    <div className="da-page" style={{ padding: "40px 48px" }}>
      <h3>Terms</h3>
      {terms.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <h4>Terms &amp; Conditions</h4>
          <ul style={{ paddingLeft: 22, margin: 0 }}>
            {terms.map((line, i) => <li key={i} style={{ marginBottom: 8, lineHeight: 1.6 }}>{line}</li>)}
          </ul>
        </div>
      )}
      {payment.length > 0 && (
        <div>
          <h4>Payment Terms</h4>
          <ul style={{ paddingLeft: 22, margin: 0 }}>
            {payment.map((line, i) => <li key={i} style={{ marginBottom: 8, lineHeight: 1.6 }}>{line}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function QuotationVoucherBodyDesignA({ id, showStamp = false, showSignature = false }) {
  const [voucher, setVoucher] = useState(null);
  const [institution, setInstitution] = useState(null);
  const authInfo = useSelector((s) => s.auth.auth_info);

  useEffect(() => {
    if (!id) return;
    salesQuotationWithRecordApi({ sale_o_id: id, from: "voucher" }).then((res) => {
      if (res?.data?.length > 0) setVoucher(res.data[0]);
    });
    getInstitutionApi().then((res) => setInstitution(res?.data || null)).catch(() => {});
  }, [id]);

  if (!voucher) return null;

  const record = voucher;
  const currency = authInfo?.currency;
  const country = authInfo?.country || "";
  const isIndia = country.toLowerCase() === "india";
  const isIndiaGst = authInfo?.gst_enabled === "yes";
  const taxLabel = isIndia ? "GST" : "VAT";
  const isUAE = country.trim().toLowerCase() === "united arab emirates";
  const taxRegLabel = isUAE ? "TRN No" : "Tax Reg No";

  const items = record.details || [];
  const quantityTotal = items.reduce((sum, d) => sum + (Number(d.item_qty) || 0), 0);

  // No dedicated "Batch" column (quotations never had one) -- item_description
  // (when present) goes as a sub-line under the item name instead.
  const usedWidth = COL_WIDTH.hash + COL_WIDTH.rate + COL_WIDTH.per + COL_WIDTH.quantity + COL_WIDTH.amount;
  const itemMaxChars = Math.max(20, Math.floor((PAGE_CONTENT_WIDTH - usedWidth - CELL_PADDING) / CHAR_WIDTH_PX));

  const columns = [
    { label: "#", width: `${COL_WIDTH.hash}px` },
    { label: "Item & Description", cellClassName: "da-item-td" },
    { label: "Rate", align: "right", width: `${COL_WIDTH.rate}px` },
    { label: "Per", align: "right", width: `${COL_WIDTH.per}px` },
    { label: "Qty", align: "right", width: `${COL_WIDTH.quantity}px` },
    { label: "Total", align: "right", width: `${COL_WIDTH.amount}px` },
  ];

  const rows = items.map((d, i) => {
    const rawAmount = Number(d.item_total || 0);
    return {
      cells: [
        String(i + 1),
        <ItemCell key="n" name={d.item_name} maxChars={itemMaxChars} batchName={d.batch_name} description={d.item_description} />,
        <span className="da-num-cell" key="r">{numberFormat(d.item_rate)}</span>,
        <span className="da-num-cell" key="p">{d.unit_symbol}</span>,
        <span className="da-num-cell" key="q">{formatPercent(d.item_qty)}</span>,
        <span className="da-amount-cell" key="a">{numberFormat(rawAmount)}</span>,
      ],
      isBonus: false,
      rawAmount,
    };
  });

  const totalsRows = [];
  totalsRows.push({ label: "Quantity Total", value: `(${quantityTotal})` });
  totalsRows.push({ label: "Sub Total", value: numberFormat(record.sub_total) });

  let taxSummary = null;
  if (record.is_cal_type === "on_total") {
    if (isIndiaGst) {
      if (record.place_of_supply_type === "intra") {
        totalsRows.push({ label: "CGST", value: numberFormatTax(record.cgst_amount) });
        totalsRows.push({ label: "SGST", value: numberFormatTax(record.sgst_amount) });
        taxSummary = { mode: "cgst_sgst", rows: [{ rate: "", taxable: numberFormat(record.sub_total), cgst: numberFormatTax(record.cgst_amount), sgst: numberFormatTax(record.sgst_amount) }] };
      } else if (record.place_of_supply_type === "inter") {
        totalsRows.push({ label: "IGST", value: numberFormatTax(record.igst_amount) });
        taxSummary = { mode: "igst", rows: [{ rate: "", taxable: numberFormat(record.sub_total), igst: numberFormatTax(record.igst_amount) }] };
      }
    } else {
      totalsRows.push({ label: taxLabel, value: numberFormat(record.total_tax) });
    }
    totalsRows.push({ label: "Discount", value: `- ${numberFormat(record.total_discount)}`, credit: true });
  }

  if (Number(record.other_service_cost) > 0) {
    totalsRows.push({ label: "Additional Expenses", value: numberFormat(record.other_service_cost) });
  }

  const grandTotal = Number(record.total_amount || 0) * Number(record.conversion_rate || 1);

  const config = {
    docTitle: "QUOTATION",
    docNumber: record.sale_order_no,
    institution: { logoUrl: institution?.logo, companyName: institution?.name || "Institution", companyGstin: null },
    party: {
      billToLabel: "Bill To",
      billTo: {
        name: record.acc_name,
        lines: [
          record.address ? `Address: ${record.address}` : null,
          record.contact_no ? `Contact No: +${record.contact_no}` : null,
        ].filter(Boolean),
      },
      fromLabel: "From",
      from: {
        name: institution?.name || "Institution",
        lines: [
          institution?.street_address || null,
          institution?.phone_no ? `+${institution.phone_no}` : null,
        ].filter(Boolean),
      },
      metaLabel: "Quotation Details",
      meta: [
        { label: "Quotation Date", value: (formatToCompanyTimezone(record.created_date) || "").split(" ")[0] },
        { label: "Quotation By", value: record.full_name || "-" },
        ...(institution?.vat_license ? [{ label: taxRegLabel, value: institution.vat_license }] : []),
      ],
    },
    columns,
    rows,
    amountFn: (slice) => numberFormat(slice.reduce((sum, r) => sum + (r.rawAmount || 0), 0)),
    totalItems: items.length,
    totalQty: quantityTotal,
    totalsRows,
    grandTotalLabel: `Grand Total (${currency || ""})`,
    grandTotalValue: numberFormat(grandTotal),
    paidValue: null,
    dueValue: null,
    amountInWordsText: convertAmountToWordsDesignA(grandTotal, { country, currency }),
    bankDetails: null,
    taxSummary,
    terms: record.narration ? `Description: ${record.narration}` : "Thank you for considering us.",
    signatory: {
      companyName: institution?.name || "Institution",
      stampUrl: showStamp ? institution?.stamp : null,
      signatureUrl: showSignature ? institution?.signature : null,
    },
    bonusNote: null,
    rowBudgets: QUOTATION_ROW_BUDGETS,
    tableClassName: "da-table--rich-item",
  };

  return (
    <>
      <VoucherBodyDesignA config={config} />
      <TermsPage record={record} />
    </>
  );
}
